import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../../components/SharedToastProvider';
import { listUrl } from '../../utils/useUrlPagination';

const initialForm = {
    districtId: '',
    location: '',
    incidentDate: '',
    sections: [],
    otherSection: '',
    notes: '',
};

const ArahanBeredar = ({ mode = 'create' }) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [form, setForm] = useState(initialForm);
    const [oydsList, setOydsList] = useState([
        { id: null, name: '', ic: '', phone: '', address: '', photo: null, media: [] },
    ]);
    const [sectionOptions, setSectionOptions] = useState([]);
    const [districtOptions, setDistrictOptions] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [isLoading, setIsLoading] = useState(mode !== 'create');
    const userName = useMemo(() => localStorage.getItem('user_name') || 'Pengguna', []);
    const userEmail = useMemo(() => localStorage.getItem('user_email') || '-', []);
    const staffNo = useMemo(() => localStorage.getItem('staff_no') || '-', []);
    const staffIc = useMemo(() => localStorage.getItem('staff_ic') || '-', []);
    const token = localStorage.getItem('token');

    useEffect(() => {
        if (!apiUrl) {
            return;
        }
        axios.get(`${apiUrl}/references/arahan-beredar-sections`)
            .then((response) => {
                setSectionOptions(response?.data?.data || []);
            })
            .catch(() => {
                setSectionOptions([]);
            });
        axios.get(`${apiUrl}/districts`)
            .then((response) => {
                setDistrictOptions(response?.data?.data || []);
            })
            .catch(() => {
                setDistrictOptions([]);
            });
    }, [apiUrl]);

    const clearValidationError = (field) => {
        setValidationErrors((prev) => {
            if (!Object.prototype.hasOwnProperty.call(prev, field)) {
                return prev;
            }
            const next = { ...prev };
            delete next[field];
            return next;
        });
        if (messageType === 'danger') {
            setMessage('');
            setMessageType('');
        }
    };

    const applyRecordToState = (data) => {
        if (!data) {
            return;
        }

        setForm({
            districtId: data.district_id ? String(data.district_id) : '',
            location: data.location || '',
            incidentDate: data.incident_date || '',
            sections: (data.sections || []).map((section) => section.id),
            otherSection: data.other_section || '',
            notes: data.notes || '',
        });
        setOydsList(
            (data.oyds || []).map((row) => ({
                id: row.id,
                name: row.name || '',
                ic: row.ic_number || '',
                phone: row.phone || '',
                address: row.address || '',
                photo: null,
                media: row.media || [],
            }))
        );
    };

    const buildMediaDownloadUrl = (media) => {
        if (!apiUrl || !id || !media?.id) {
            return '';
        }
        return `${apiUrl}/arahan-beredar/${id}/oyd-media/${media.id}/download`;
    };

    const openMedia = async (media) => {
        const downloadUrl = buildMediaDownloadUrl(media);
        if (!downloadUrl) {
            toast.error('Lampiran tidak dijumpai.');
            return;
        }

        try {
            const response = await axios.get(downloadUrl, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                responseType: 'blob',
            });
            const contentType = response.headers?.['content-type'] || media?.file_type || 'application/octet-stream';
            const blob = new Blob([response.data], { type: contentType });
            const blobUrl = window.URL.createObjectURL(blob);
            const opened = window.open(blobUrl, '_blank', 'noopener,noreferrer');

            if (!opened) {
                const link = document.createElement('a');
                link.href = blobUrl;
                link.target = '_blank';
                link.rel = 'noreferrer';
                link.download = media?.file_name || 'lampiran';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
        } catch (_error) {
            toast.error('Gagal membuka lampiran OYDS.');
        }
    };

    const toggleSection = (value) => {
        setForm((prev) => {
            const exists = prev.sections.includes(value);
            return {
                ...prev,
                sections: exists
                    ? prev.sections.filter((item) => item !== value)
                    : [...prev.sections, value],
            };
        });
        clearValidationError('sections');
    };

    const updateOyds = (index, field, value) => {
        setOydsList((prev) => prev.map((item, idx) => (
            idx === index ? { ...item, [field]: value } : item
        )));
        clearValidationError('oyds');
    };

    const addOyds = () => {
        setOydsList((prev) => [
            ...prev,
            { id: null, name: '', ic: '', phone: '', address: '', photo: null, media: [] },
        ]);
    };

    const removeOyds = (index) => {
        setOydsList((prev) => (
            prev.length === 1 ? prev : prev.filter((_, idx) => idx !== index)
        ));
    };

    useEffect(() => {
        if (mode === 'create' || !apiUrl || !id) {
            return;
        }

        setIsLoading(true);
        axios.get(`${apiUrl}/arahan-beredar/${id}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        })
            .then((response) => {
                const data = response?.data?.data;
                if (!data) {
                    return;
                }
                applyRecordToState(data);
            })
            .catch(() => {
                setMessageType('danger');
                setMessage('Gagal memuatkan rekod arahan beredar.');
            })
            .finally(() => setIsLoading(false));
    }, [apiUrl, id, mode, token]);

    const handleSubmit = () => {
        if (!apiUrl) {
            setMessageType('danger');
            setMessage('API URL tidak diset.');
            return;
        }
        const nextErrors = {};
        if (!String(form.districtId || '').trim()) {
            nextErrors.district_id = 'Daerah wajib dipilih.';
        }
        if (!String(form.location || '').trim()) {
            nextErrors.location = 'Lokasi kejadian wajib diisi.';
        }
        if (!String(form.incidentDate || '').trim()) {
            nextErrors.incident_date = 'Tarikh kejadian wajib diisi.';
        }
        if (!form.sections.length) {
            nextErrors.sections = 'Sila pilih sekurang-kurangnya satu Seksyen Kesalahan.';
        }
        if (!oydsList.length || !oydsList.some((row) => (
            String(row.name || '').trim()
            || String(row.ic || '').trim()
            || String(row.phone || '').trim()
            || String(row.address || '').trim()
            || (row.photo && row.photo.length)
            || (Array.isArray(row.media) && row.media.length)
        ))) {
            nextErrors.oyds = 'Sila isi sekurang-kurangnya satu maklumat OYDS.';
        }
        if (Object.keys(nextErrors).length > 0) {
            setValidationErrors(nextErrors);
            setMessageType('danger');
            setMessage('Sila lengkapkan maklumat wajib yang ditanda merah.');
            return;
        }
        setValidationErrors({});
        setIsSubmitting(true);
        setMessage('');
        setMessageType('');

        const payload = new FormData();
        payload.append('district_id', form.districtId || '');
        payload.append('location', form.location);
        payload.append('incident_date', form.incidentDate);
        payload.append('other_section', form.otherSection || '');
        payload.append('notes', form.notes || '');
        payload.append('status', 'draf');
        payload.append('oyds', JSON.stringify(
            oydsList.map((row) => ({
                id: row.id,
                name: row.name,
                ic_number: row.ic,
                phone: row.phone,
                address: row.address,
            }))
        ));
        form.sections.forEach((sectionId) => {
            payload.append('sections[]', sectionId);
        });
        oydsList.forEach((row, index) => {
            if (!row.photo) {
                return;
            }
            Array.from(row.photo).forEach((file) => {
                payload.append(`oyds_photos[${index}][]`, file);
            });
        });

        const request = mode === 'edit'
            ? (() => {
                payload.append('_method', 'PUT');
                return axios.post(`${apiUrl}/arahan-beredar/${id}`, payload, {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                });
            })()
            : axios.post(`${apiUrl}/arahan-beredar`, payload, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            });

        request
            .then((response) => {
                const successMessage = mode === 'edit' ? 'Rekod arahan beredar dikemaskini.' : 'Rekod arahan beredar disimpan.';
                applyRecordToState(response?.data?.data);
                setMessageType('success');
                setMessage(successMessage);
                toast.success(successMessage);
            })
            .catch((error) => {
                const apiErrors = error?.response?.data?.errors || {};
                if (Object.keys(apiErrors).length > 0) {
                    setValidationErrors(apiErrors);
                    setMessageType('danger');
                    setMessage('Sila lengkapkan maklumat wajib yang ditanda merah.');
                    return;
                }
                setMessageType('danger');
                setMessage(error?.response?.data?.message || 'Gagal menyimpan rekod.');
            })
            .finally(() => setIsSubmitting(false));
    };

    return (
        <div className="app-complaints app-beredar-form-page">
            <div className="app-complaints-header">
                <div>
                    <span className="app-eyebrow">Rekod Arahan Beredar</span>
                    <h3>Rekod Tindakan Nasihat & Arahan Beredar (AB 2026)</h3>
                    <p>Unit Perundangan dan Kesalahan BPN HQ.</p>
                </div>
            </div>

            {message && (
                <div className={`app-form-alert app-form-alert-${messageType}`}>
                    {message}
                </div>
            )}

            {isLoading && (
                <div className="app-empty">Memuatkan rekod...</div>
            )}

            {!isLoading && (
            <>
            <div className="app-card">
                <div className="app-card-header">
                    <h4>Maklumat Kejadian</h4>
                    <span className="app-pill">Wajib</span>
                </div>
                <div className="app-form-grid">
                    <label className="app-form-field">
                        <span>Daerah</span>
                        <select
                            value={form.districtId}
                            className={validationErrors.district_id ? 'app-input-error' : ''}
                            onChange={(event) => {
                                setForm({ ...form, districtId: event.target.value });
                                clearValidationError('district_id');
                            }}
                        >
                            <option value="">Pilih Daerah</option>
                            {districtOptions.map((district) => (
                                <option key={district.id} value={district.id}>{district.name}</option>
                            ))}
                        </select>
                        {validationErrors.district_id && <small className="app-inline-note app-inline-note-error">{Array.isArray(validationErrors.district_id) ? validationErrors.district_id[0] : validationErrors.district_id}</small>}
                    </label>
                    <label className="app-form-field">
                        <span>Lokasi Kejadian</span>
                        <input
                            type="text"
                            placeholder="Masukkan lokasi kejadian"
                                value={form.location}
                                className={validationErrors.location ? 'app-input-error' : ''}
                                onChange={(event) => {
                                    setForm({ ...form, location: event.target.value });
                                    clearValidationError('location');
                                }}
                            />
                        {validationErrors.location && <small className="app-inline-note app-inline-note-error">{Array.isArray(validationErrors.location) ? validationErrors.location[0] : validationErrors.location}</small>}
                    </label>
                    <label className="app-form-field">
                        <span>Tarikh Kejadian</span>
                        <input
                            type="date"
                                value={form.incidentDate}
                                className={validationErrors.incident_date ? 'app-input-error' : ''}
                                onChange={(event) => {
                                    setForm({ ...form, incidentDate: event.target.value });
                                    clearValidationError('incident_date');
                                }}
                            />
                        {validationErrors.incident_date && <small className="app-inline-note app-inline-note-error">{Array.isArray(validationErrors.incident_date) ? validationErrors.incident_date[0] : validationErrors.incident_date}</small>}
                    </label>
                </div>
            </div>

            <div className="app-card">
                <div className="app-card-header">
                    <h4>Kesalahan</h4>
                    <span className="app-pill">Wajib</span>
                </div>
                <div className="app-form-section">
                    <p>Nyatakan seksyen kesalahan undang-undang yang berkaitan.</p>
                </div>
                <div className={`app-checkbox-grid ${validationErrors.sections ? 'app-input-error' : ''}`}>
                    {sectionOptions.map((option) => (
                        <label key={option.id} className="app-checkbox">
                            <input
                                type="checkbox"
                                checked={form.sections.includes(option.id)}
                                onChange={() => toggleSection(option.id)}
                            />
                            {option.name}
                        </label>
                    ))}
                </div>
                {validationErrors.sections && <small className="app-inline-note app-inline-note-error">{Array.isArray(validationErrors.sections) ? validationErrors.sections[0] : validationErrors.sections}</small>}
                <div className="app-form-grid" style={{ marginTop: '1rem' }}>
                    <label className="app-form-field app-span-full">
                        <span>Lain-lain Kesalahan (sila nyatakan)</span>
                        <input
                            type="text"
                            placeholder="Jika berkaitan"
                            value={form.otherSection}
                            onChange={(event) => setForm({ ...form, otherSection: event.target.value })}
                        />
                    </label>
                    <label className="app-form-field app-span-full">
                        <span>Ulasan PPA Bertugas (jika perlu)</span>
                        <textarea
                            rows={4}
                            placeholder="Catatan tambahan pegawai bertugas"
                            value={form.notes}
                            onChange={(event) => setForm({ ...form, notes: event.target.value })}
                        ></textarea>
                    </label>
                </div>
            </div>

            <div className="app-card">
                <div className="app-card-header">
                    <h4>Maklumat OYDS</h4>
                    <span className="app-pill">Wajib</span>
                </div>
                <div className="app-oyds-table-wrap app-beredar-oyds-table">
                    <div className="app-oyds-table-head">
                        <div>Nama OYDS</div>
                        <div>No. K/P</div>
                        <div>No. Telefon</div>
                        <div>Alamat</div>
                        <div>Gambar</div>
                        <div>Aksi</div>
                    </div>
                    {oydsList.map((row, index) => (
                        <div key={`oyds-${index}`} className="app-oyds-table-row">
                            <div className="app-oyds-table-cell">
                                <input
                                    type="text"
                                    placeholder="Nama penuh"
                                    value={row.name}
                                    onChange={(event) => updateOyds(index, 'name', event.target.value)}
                                />
                            </div>
                            <div className="app-oyds-table-cell">
                                <input
                                    type="text"
                                    placeholder="XXXXXX-XX-XXXX"
                                    value={row.ic}
                                    onChange={(event) => updateOyds(index, 'ic', event.target.value)}
                                />
                            </div>
                            <div className="app-oyds-table-cell">
                                <input
                                    type="text"
                                    placeholder="No telefon"
                                    value={row.phone}
                                    onChange={(event) => updateOyds(index, 'phone', event.target.value)}
                                />
                            </div>
                            <div className="app-oyds-table-cell">
                                <input
                                    type="text"
                                    placeholder="Alamat terkini"
                                    value={row.address}
                                    onChange={(event) => updateOyds(index, 'address', event.target.value)}
                                />
                            </div>
                            <div className="app-oyds-table-cell app-beredar-oyds-media-cell">
                                <input
                                    type="file"
                                    multiple
                                    onChange={(event) => updateOyds(index, 'photo', event.target.files)}
                                />
                                {row.media?.length > 0 && (
                                    <div className="app-oyds-attachment-strip app-beredar-oyds-attachment-strip">
                                        {row.media.map((media) => (
                                            <button
                                                type="button"
                                                key={media.id}
                                                className="app-oyds-attachment-pill-main app-beredar-oyds-attachment-link"
                                                title={media.file_name}
                                                onClick={() => openMedia(media)}
                                            >
                                                <span className="app-oyds-attachment-pill-thumb">
                                                    <i className="bi bi-paperclip"></i>
                                                </span>
                                                <span className="app-oyds-attachment-pill-name">{media.file_name}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="app-oyds-table-cell app-oyds-table-cell-action">
                                <button
                                    type="button"
                                    className="app-icon-button app-icon-button-danger"
                                    onClick={() => removeOyds(index)}
                                    title="Buang OYDS"
                                    aria-label="Buang OYDS"
                                >
                                    <i className="bi bi-trash"></i>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                {validationErrors.oyds && <small className="app-inline-note app-inline-note-error">{Array.isArray(validationErrors.oyds) ? validationErrors.oyds[0] : validationErrors.oyds}</small>}
                <div className="app-inline-add">
                    <button className="app-button app-button-ghost" type="button" onClick={addOyds}>
                        <i className="bi bi-plus-lg"></i>
                        Tambah OYDS
                    </button>
                </div>
            </div>

            <div className="app-card">
                <div className="app-card-header">
                    <h4>Maklumat Pegawai Bertugas</h4>
                    <span className="app-pill">Auto</span>
                </div>
                <div className="app-form-grid">
                    <div className="app-form-field">
                        <span>Email</span>
                        <div className="app-detail-value">{userEmail}</div>
                    </div>
                    <div className="app-form-field">
                        <span>Nama PPA Bertugas</span>
                        <div className="app-detail-value">{userName}</div>
                    </div>
                    <div className="app-form-field">
                        <span>Seksyen Bertugas</span>
                        <div className="app-detail-value">-</div>
                    </div>
                    <div className="app-form-field">
                        <span>No. KP / No. BPN</span>
                        <div className="app-detail-value">
                            {staffNo !== '-' && staffNo !== '' ? staffNo : staffIc}
                        </div>
                    </div>
                </div>
            </div>

            <div className="app-actions-sticky app-beredar-sticky-actions">
                <div className="app-complaints-actions app-beredar-sticky-actions-row">
                    <button className="app-button" type="button" onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? 'Menyimpan...' : (mode === 'edit' ? 'Kemaskini Rekod' : 'Simpan Rekod')}
                    </button>
                    {mode === 'edit' && (
                        <button
                            className="app-button app-button-ghost"
                            type="button"
                            onClick={() => navigate(listUrl('/app/arahan-beredar'))}
                        >
                            Kembali
                        </button>
                    )}
                </div>
            </div>
            </>
            )}
        </div>
    );
};

export default ArahanBeredar;
